import React, { useMemo, useRef, useEffect } from 'react';
import { CardBody, Input, Badge, Spinner } from 'reactstrap';
import { COUNTER_ACCENT } from '../../../../../shared/offer-domain/constants';
import CounterOfferModal from '../CounterOfferModal';
import { getRowKey } from '../../../hooks/modelDeliveryTable/shared';
import {
  formatIncotermRoute,
  formatParcelSize,
  hasCounterOffer,
  isCounterValueChanged,
  isCounterDateChanged,
} from '../shared/utils';
import { buildLpgOfferTableModel } from '../shared/lpgTableModel';
import {
  cellClass,
  headerCellClass,
  renderLpgPortCell,
  createNumberInputRenderer,
} from '../shared/tableRenderHelpers';
import { getCargoRatioKey } from '../../../../TenderOpening/components/Detail/HpsParcel/lpg/utils';

const LPG_UNIT = 'MT';
const getTableColSpan = (isDeliveryEditable, requiresCargoRatio) => {
  let cols = 8; // expand, incoterm, port1-3, parcel, capable, alpha
  if (requiresCargoRatio) cols += 1;
  if (isDeliveryEditable) cols += 1;
  return cols;
};

const ModelDeliveryTableLpg = ({
  embedded = false,
  isDeliveryEditable,
  requiresCargoRatio = false,
  isLoading = false,
  searchTerm,
  setSearchTerm,
  filteredItems,
  capableState,
  expandedRows,
  counterModalOpen,
  counterFormData,
  counterHasChanges,
  counterFormErrors,
  counterFormLoading,
  form,
  incotermTypeOptions,
  incotermRouteOptions,
  incotermLocationOptions,
  fobRouteOptions,
  handleInputChange,
  handleCapableChange,
  toggleRowExpand,
  handleCounterFormChange,
  handleSaveCounter,
  handleDeleteCounter,
  openCounterModal,
  closeCounterModal,
}) => {
  const handleInputChangeRef = useRef(handleInputChange);
  useEffect(() => {
    handleInputChangeRef.current = handleInputChange;
  }, [handleInputChange]);

  const renderNumberInput = useMemo(
    () =>
      createNumberInputRenderer({
        isDeliveryEditable,
        handleInputChange: (e, rowKey, field, type, row) =>
          handleInputChangeRef.current(e, rowKey, field, type, row),
      }),
    [isDeliveryEditable]
  );

  const tableRows = useMemo(
    () => buildLpgOfferTableModel(filteredItems, LPG_UNIT),
    [filteredItems]
  );

  const counterByGroupId = useMemo(() => {
    const map = {};
    tableRows.forEach(row => {
      map[row.groupId] = map[row.groupId] || hasCounterOffer(row.data);
    });
    return map;
  }, [tableRows]);

  const colSpan = getTableColSpan(isDeliveryEditable, requiresCargoRatio);

  const renderCounterOfferRow = (data, groupMeta) => {
    const incotermRoute = formatIncotermRoute(
      data.counterIncoterm,
      data.counterRoute
    );
    const originalIncotermRoute = formatIncotermRoute(
      data.incoterm,
      data.route
    );
    const incotermRouteChanged = incotermRoute !== originalIncotermRoute;

    const parcelByKey = new Map();
    (filteredItems || []).forEach(item => {
      if (
        String(item.quantityIncotermId ?? '') !==
        String(data.quantityIncotermId ?? '')
      ) {
        return;
      }
      const parcelKey = String(
        item.parcelSizeLabel ?? item.parcelSize ?? item.deliveryBasis ?? ''
      );
      if (!parcelKey) return;
      const current = parcelByKey.get(parcelKey);
      if (
        !current ||
        (!current.quantityInformationDetailCounterId &&
          item.quantityInformationDetailCounterId)
      ) {
        parcelByKey.set(parcelKey, item);
      }
    });

    const masterParcelOrder = groupMeta?.parcelSizes ?? [];
    const parcelItems = [];
    if (masterParcelOrder.length) {
      const usedKeys = new Set();
      masterParcelOrder.forEach(label => {
        const key = String(label);
        if (parcelByKey.has(key)) {
          parcelItems.push(parcelByKey.get(key));
          usedKeys.add(key);
          return;
        }
        for (const [parcelKey, item] of parcelByKey.entries()) {
          if (usedKeys.has(parcelKey)) continue;
          const itemLabel = String(
            item.parcelSizeLabel ??
              formatParcelSize(item.parcelSize ?? item.deliveryBasis, 'MT')
          );
          if (itemLabel === key) {
            parcelItems.push(item);
            usedKeys.add(parcelKey);
            break;
          }
        }
      });
      parcelByKey.forEach((item, key) => {
        if (!usedKeys.has(key)) parcelItems.push(item);
      });
    } else {
      parcelByKey.forEach(item => parcelItems.push(item));
    }

    if (!parcelItems.length) {
      if (masterParcelOrder.length) {
        masterParcelOrder.forEach(label => {
          parcelItems.push({ ...data, parcelSizeLabel: label });
        });
      } else {
        parcelItems.push(data);
      }
    }

    const counterRatiosFromGroup = (filteredItems || []).find(
      item =>
        String(item.quantityIncotermId ?? '') ===
          String(data.quantityIncotermId ?? '') &&
        Array.isArray(item.counterCargoRatios) &&
        item.counterCargoRatios.length
    )?.counterCargoRatios;

    const counterRatios =
      Array.isArray(data.counterCargoRatios) && data.counterCargoRatios.length
        ? data.counterCargoRatios
        : Array.isArray(counterRatiosFromGroup) && counterRatiosFromGroup.length
          ? counterRatiosFromGroup
          : [{ id: null, label: null }];

    const counterRows = [];
    counterRatios.forEach((ratio, ratioIndex) => {
      parcelItems.forEach((parcelItem, parcelIndex) => {
        const ratioKey = getCargoRatioKey(ratio?.id);
        const parcelRatio =
          (parcelItem.counterCargoRatios || []).find(
            entry =>
              String(entry.quantityInformationCargoRatioFK ?? '') ===
                String(parcelItem.quantityInformationCargoRatioId ?? '') ||
              getCargoRatioKey(entry.id) === ratioKey
          ) || null;
        const mergedRatio = parcelRatio
          ? {
              ...ratio,
              ...parcelRatio,
              id: ratio?.id ?? parcelRatio.id,
              label: ratio?.label ?? parcelRatio.label,
              alpha: parcelRatio.alpha ?? parcelItem.counterAlpha ?? '',
            }
          : {
              ...ratio,
              alpha: parcelItem.counterAlpha ?? '',
            };
        counterRows.push({
          ratio: mergedRatio,
          parcelItem,
          showGroupCells: ratioIndex === 0 && parcelIndex === 0,
          showRatioCell: requiresCargoRatio && parcelIndex === 0,
          groupRowSpan: counterRatios.length * parcelItems.length,
          ratioRowSpan: parcelItems.length,
          parcelSizes: groupMeta?.parcelSizes || [],
        });
      });
    });

    return (
      <tr className='bg-light'>
        <td colSpan={colSpan} className='p-0 border-0'>
          <div
            className='bg-light-subtle'
            style={{
              borderLeft: `3px solid ${COUNTER_ACCENT}`,
              maxWidth: '100%',
              overflow: 'hidden',
            }}
          >
            <div
              className='px-2 py-2'
              style={{ width: '100%', overflowX: 'auto' }}
            >
              <table
                className='table table-sm table-borderless mb-0 align-middle w-100'
                style={{ tableLayout: 'fixed', width: '100%', minWidth: 0 }}
              >
                <thead>
                  <tr className='text-muted border-bottom'>
                    <th
                      className='fs-12 fw-semibold ps-1 pb-2'
                      style={{ width: '9%' }}
                    />
                    <th
                      className='fs-12 fw-semibold pb-2 pe-2'
                      style={{ width: requiresCargoRatio ? '16%' : '18%' }}
                    >
                      Incoterm - Route
                    </th>
                    <th
                      className='fs-12 fw-semibold pb-2 pe-2'
                      style={{ width: requiresCargoRatio ? '12%' : '14%' }}
                    >
                      Port 1
                    </th>
                    <th
                      className='fs-12 fw-semibold pb-2 pe-2'
                      style={{ width: requiresCargoRatio ? '10%' : '12%' }}
                    >
                      Port 2
                    </th>
                    <th
                      className='fs-12 fw-semibold pb-2 pe-2'
                      style={{ width: requiresCargoRatio ? '8%' : '10%' }}
                    >
                      Port 3
                    </th>
                    {requiresCargoRatio && (
                      <th
                        className='fs-12 fw-semibold pb-2 pe-2'
                        style={{ width: '10%' }}
                      >
                        Cargo Ratio
                      </th>
                    )}
                    <th
                      className='fs-12 fw-semibold pb-2 pe-2'
                      style={{ width: requiresCargoRatio ? '10%' : '12%' }}
                    >
                      Parcel Size
                    </th>
                    <th
                      className='fs-12 fw-semibold pb-2 pe-2 text-center'
                      style={{ width: requiresCargoRatio ? '8%' : '10%' }}
                    >
                      Is Capable?
                    </th>
                    <th
                      className='fs-12 fw-semibold pb-2 pe-2'
                      style={{ width: requiresCargoRatio ? '12%' : '14%' }}
                    >
                      Alpha
                    </th>
                    <th
                      className='fs-12 fw-semibold text-center pb-2 pe-1'
                      style={{ width: '7%' }}
                    />
                  </tr>
                </thead>
                <tbody>
                  {counterRows.map((row, rowIndex) => (
                    <tr key={`counter-${row.ratio?.id ?? 'x'}-${rowIndex}`}>
                      {row.showGroupCells && (
                        <td
                          rowSpan={row.groupRowSpan}
                          className='ps-1 py-2 align-top'
                        >
                          <span
                            className='badge rounded-pill px-2 py-1 fw-semibold'
                            style={{
                              backgroundColor: COUNTER_ACCENT,
                              color: '#fff',
                              fontSize: '11px',
                            }}
                          >
                            Counter
                          </span>
                        </td>
                      )}
                      {row.showGroupCells && (
                        <td
                          rowSpan={row.groupRowSpan}
                          className={`fs-12 py-2 pe-2 align-top text-break ${incotermRouteChanged ? 'fw-semibold' : 'fw-medium'}`}
                          style={{
                            color: incotermRouteChanged
                              ? COUNTER_ACCENT
                              : undefined,
                            wordBreak: 'break-word',
                          }}
                        >
                          {incotermRoute}
                        </td>
                      )}
                      {row.showGroupCells && (
                        <td
                          rowSpan={row.groupRowSpan}
                          className={`${cellClass} align-top`}
                          style={{ minWidth: 0 }}
                        >
                          {renderLpgPortCell(
                            data.counterFobLocationNames?.length
                              ? null
                              : data.counterLoadingPort,
                            groupMeta?.parcelSizes ?? [],
                            {
                              startDate: data.counterStartDate,
                              endDate: data.counterEndDate,
                              fallbackDate: data.counterAddAld,
                              locationNames: data.counterFobLocationNames,
                              route: data.counterRoute,
                              highlight: {
                                port: isCounterValueChanged(
                                  data.port1 || data.loadingPort,
                                  data.counterLoadingPort
                                ),
                                parcelSize: false,
                                date: isCounterDateChanged(
                                  data.port1StartDate,
                                  data.port1EndDate,
                                  data.counterStartDate,
                                  data.counterEndDate,
                                  data.addAld,
                                  data.counterAddAld
                                ),
                              },
                            }
                          )}
                        </td>
                      )}
                      {row.showGroupCells && (
                        <td rowSpan={row.groupRowSpan} className={cellClass} />
                      )}
                      {row.showGroupCells && (
                        <td rowSpan={row.groupRowSpan} className={cellClass} />
                      )}
                      {row.showRatioCell && (
                        <td
                          rowSpan={row.ratioRowSpan}
                          className={`${cellClass} fs-13 align-top`}
                        >
                          {row.ratio?.label ? (
                            <span
                              className='badge bg-danger-subtle text-danger border border-danger-subtle fw-semibold rounded-3'
                              style={{ fontSize: '12px' }}
                            >
                              {row.ratio.label}
                            </span>
                          ) : (
                            <span className='text-muted'>-</span>
                          )}
                        </td>
                      )}
                      <td
                        className={`${cellClass} fs-13 fw-semibold align-top`}
                      >
                        {row.parcelItem.parcelSizeLabel ||
                          formatParcelSize(
                            row.parcelItem.parcelSize ??
                              row.parcelItem.deliveryBasis,
                            LPG_UNIT
                          )}
                      </td>
                      {row.showGroupCells && (
                        <td
                          rowSpan={row.groupRowSpan}
                          className={`${cellClass} align-top`}
                        />
                      )}
                      <td
                        className={`${cellClass} align-top`}
                        style={{ minWidth: 0 }}
                      >
                        {(() => {
                          const ratioKey = getCargoRatioKey(row.ratio?.id);
                          const ratioEntry = (
                            row.parcelItem.counterCargoRatios || []
                          ).find(
                            ratio =>
                              String(
                                ratio.quantityInformationCargoRatioFK ?? ''
                              ) ===
                                String(
                                  row.parcelItem
                                    .quantityInformationCargoRatioId ?? ''
                                ) || getCargoRatioKey(ratio.id) === ratioKey
                          );
                          const counterAlpha =
                            ratioEntry?.alpha ??
                            row.parcelItem.counterRatioAlphaByKey?.[ratioKey] ??
                            '';
                          const pricingRow = {
                            ...row.parcelItem,
                            cargoRatio:
                              row.ratio?.id ?? row.parcelItem.cargoRatio,
                            counterCargoRatioId: row.ratio?.id ?? null,
                            counterCargoRatioLabel: row.ratio?.label ?? null,
                            counterCargoRatioRecordId:
                              ratioEntry?.recordId ??
                              row.ratio?.recordId ??
                              null,
                            quantityInformationCargoRatioId:
                              ratioEntry?.quantityInformationCargoRatioFK ??
                              row.ratio?.quantityInformationCargoRatioFK ??
                              row.parcelItem.quantityInformationCargoRatioId ??
                              null,
                            counterAlpha,
                          };
                          const pricingRowKey = getRowKey(pricingRow);
                          return renderNumberInput({
                            rowKey: pricingRowKey,
                            row: pricingRow,
                            field: 'counterAlpha',
                            value: pricingRow.counterAlpha,
                            disabled: !isDeliveryEditable,
                            suffix: 'USD/MT',
                          });
                        })()}
                      </td>
                      {row.showGroupCells && (
                        <td
                          rowSpan={row.groupRowSpan}
                          className='text-center py-2 pe-1 align-top'
                        >
                          {isDeliveryEditable && (
                            <div className='d-flex justify-content-center gap-1 flex-wrap'>
                              <button
                                type='button'
                                className='btn btn-soft-primary btn-sm btn-icon'
                                title='Edit Counter Offer'
                                onClick={() => openCounterModal(data)}
                              >
                                <i className='ri-edit-line align-bottom' />
                              </button>
                              <button
                                type='button'
                                className='btn btn-soft-danger btn-sm btn-icon'
                                title='Delete Counter Offer'
                                onClick={() => handleDeleteCounter(data)}
                              >
                                <i className='ri-delete-bin-line align-bottom' />
                              </button>
                            </div>
                          )}
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </td>
      </tr>
    );
  };

  const renderNoCounterOfferRow = () => (
    <tr className='bg-light'>
      <td colSpan={colSpan} className='p-0 border-0'>
        <div
          className='px-4 py-4 text-muted fs-13 text-center'
          style={{ borderLeft: `3px solid ${COUNTER_ACCENT}` }}
        >
          There is no counteroffer for the requirement mentioned above.
        </div>
      </td>
    </tr>
  );

  return (
    <>
      <CardBody className={embedded ? 'p-3' : 'px-0'}>
        {!embedded && (
          <div className='d-flex align-items-center mb-3'>
            <div className='position-relative' style={{ width: '280px' }}>
              <i className='ri-search-line position-absolute top-50 translate-middle-y text-muted ms-3' />
              <Input
                type='text'
                placeholder='Search'
                className='ps-5 rounded-3'
                style={{ height: '36px' }}
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
              />
            </div>
          </div>
        )}

        <div className='table-responsive table-card border rounded-3'>
          <table
            className={`table table-hover table-centered align-middle mb-0 ${embedded ? '' : 'table-nowrap'}`}
            style={{ minWidth: embedded ? '1180px' : undefined }}
          >
            <thead className='table-light'>
              <tr>
                <th className={headerCellClass} style={{ width: '44px' }} />
                <th className={headerCellClass} style={{ minWidth: '130px' }}>
                  Incoterm - Route
                </th>
                <th className={headerCellClass} style={{ minWidth: '200px' }}>
                  Port 1
                </th>
                <th className={headerCellClass} style={{ minWidth: '160px' }}>
                  Port 2
                </th>
                <th className={headerCellClass} style={{ minWidth: '120px' }}>
                  Port 3
                </th>
                {requiresCargoRatio && (
                  <th className={headerCellClass} style={{ minWidth: '110px' }}>
                    Cargo Ratio
                  </th>
                )}
                <th className={headerCellClass} style={{ minWidth: '110px' }}>
                  Parcel Size
                </th>
                <th
                  className={`${headerCellClass} text-center`}
                  style={{ minWidth: '100px' }}
                >
                  Is Capable?
                </th>
                <th className={headerCellClass} style={{ minWidth: '150px' }}>
                  Alpha
                </th>
                {isDeliveryEditable && (
                  <th
                    className={`${headerCellClass} text-center`}
                    style={{ minWidth: '90px' }}
                  >
                    Counter Offer
                  </th>
                )}
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td colSpan={colSpan} className='text-center py-4'>
                    <Spinner size='sm' color='primary' />
                  </td>
                </tr>
              ) : tableRows.length > 0 ? (
                tableRows.map(row => {
                  const { data, groupId, groupMeta } = row;
                  const rowKey = getRowKey(data);
                  const isCapable = capableState[rowKey] || false;
                  const isExpanded = expandedRows[groupId];
                  const showCounter = hasCounterOffer(data);
                  const groupHasCounter = !!counterByGroupId[groupId];
                  const showCounterButton =
                    row.showGroupCells &&
                    isDeliveryEditable &&
                    !groupHasCounter &&
                    tableRows
                      .filter(r => r.groupId === groupId)
                      .some(r => !(capableState[getRowKey(r.data)] || false));

                  return (
                    <React.Fragment key={rowKey}>
                      <tr
                        className={
                          isExpanded && row.showGroupCells
                            ? 'border-bottom-1'
                            : undefined
                        }
                        style={
                          isExpanded || groupHasCounter
                            ? { borderLeft: `3px solid ${COUNTER_ACCENT}` }
                            : undefined
                        }
                      >
                        {row.showGroupCells && (
                          <td rowSpan={row.groupRowSpan} className={cellClass}>
                            <button
                              type='button'
                              className='btn btn-link btn-sm p-0 text-muted'
                              onClick={() => toggleRowExpand(groupId)}
                            >
                              <i
                                className={`ri-arrow-${isExpanded ? 'down' : 'right'}-s-line fs-16`}
                              />
                            </button>
                          </td>
                        )}

                        {row.showGroupCells && (
                          <td
                            rowSpan={row.groupRowSpan}
                            className={`${cellClass} fs-13`}
                          >
                            {groupMeta.incotermRoute}
                          </td>
                        )}

                        {row.showGroupCells && (
                          <td rowSpan={row.groupRowSpan} className={cellClass}>
                            {renderLpgPortCell(
                              groupMeta.port1,
                              groupMeta.parcelSizes,
                              {
                                portLabel: 'Location',
                                startDate: groupMeta.port1StartDate,
                                endDate: groupMeta.port1EndDate,
                                fallbackDate: groupMeta.addAld,
                                locationNames: groupMeta.port1Names,
                                route: groupMeta.route,
                              }
                            )}
                          </td>
                        )}

                        {row.showGroupCells && (
                          <td rowSpan={row.groupRowSpan} className={cellClass}>
                            {renderLpgPortCell(
                              groupMeta.port2,
                              groupMeta.port2 ? groupMeta.parcelSizes : [],
                              {
                                startDate: groupMeta.port2StartDate,
                                endDate: groupMeta.port2EndDate,
                                fallbackDate: groupMeta.addAld2,
                              }
                            )}
                          </td>
                        )}

                        {row.showGroupCells && (
                          <td rowSpan={row.groupRowSpan} className={cellClass}>
                            {renderLpgPortCell(
                              groupMeta.port3,
                              groupMeta.port3 ? groupMeta.parcelSizes : [],
                              {
                                startDate: groupMeta.port3StartDate,
                                endDate: groupMeta.port3EndDate,
                                fallbackDate: groupMeta.addAld3,
                              }
                            )}
                          </td>
                        )}

                        {requiresCargoRatio && row.showCargoRatioCell && (
                          <td
                            rowSpan={row.cargoRatioRowSpan}
                            className={`${cellClass} fs-13`}
                          >
                            {row.cargoRatioLabel ? (
                              <span
                                className='badge bg-primary-subtle text-primary border border-primary-subtle fw-semibold rounded-3'
                                style={{ fontSize: '12px' }}
                              >
                                {row.cargoRatioLabel}
                              </span>
                            ) : (
                              <span className='text-muted'>-</span>
                            )}
                          </td>
                        )}

                        <td className={`${cellClass} fs-13 fw-semibold`}>
                          {data.parcelSizeLabel ||
                            formatParcelSize(
                              data.parcelSize ?? data.deliveryBasis,
                              LPG_UNIT
                            )}
                        </td>

                        <td className={`${cellClass} text-center`}>
                          {!isDeliveryEditable ? (
                            isCapable ? (
                              <Badge
                                color='primary'
                                className='px-2 py-1 fs-12 fw-medium rounded-pill'
                              >
                                Compliant
                              </Badge>
                            ) : groupHasCounter ? (
                              <span
                                className='badge rounded-pill px-2 py-1 fs-12 fw-medium'
                                style={{
                                  backgroundColor: COUNTER_ACCENT,
                                  color: '#fff',
                                }}
                              >
                                Countered
                              </span>
                            ) : (
                              <Badge
                                color='secondary'
                                className='px-2 py-1 fs-12 fw-medium rounded-pill'
                              >
                                Not Offered
                              </Badge>
                            )
                          ) : (
                            <div className='form-check form-switch d-flex justify-content-center mb-0'>
                              <Input
                                className='form-check-input'
                                type='checkbox'
                                role='switch'
                                checked={showCounter ? false : isCapable}
                                disabled={showCounter}
                                onChange={e =>
                                  handleCapableChange(
                                    e,
                                    rowKey,
                                    data.quantityInformationDetailId
                                  )
                                }
                              />
                            </div>
                          )}
                        </td>

                        <td className={cellClass}>
                          {renderNumberInput({
                            rowKey,
                            row: data,
                            field: 'alpha',
                            value: data.alpha || '',
                            disabled: !isCapable,
                            suffix: 'USD/MT',
                          })}
                        </td>

                        {row.showGroupCells && isDeliveryEditable && (
                          <td
                            rowSpan={row.groupRowSpan}
                            className={`${cellClass} text-center`}
                          >
                            {showCounterButton && (
                              <button
                                type='button'
                                className='btn btn-soft-primary btn-sm btn-icon'
                                title='Counter Offer'
                                onClick={() =>
                                  openCounterModal(
                                    groupMeta.representative || data
                                  )
                                }
                              >
                                <i className='ri-edit-line align-bottom' />
                              </button>
                            )}
                          </td>
                        )}
                      </tr>

                      {row.isLastInGroup &&
                        isExpanded &&
                        (groupHasCounter
                          ? renderCounterOfferRow(
                              groupMeta.representative || data,
                              groupMeta
                            )
                          : renderNoCounterOfferRow())}
                    </React.Fragment>
                  );
                })
              ) : (
                <tr>
                  <td colSpan={colSpan} className='text-center text-muted py-4'>
                    {searchTerm
                      ? 'No data matching your search'
                      : 'No data available'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </CardBody>

      <CounterOfferModal
        isOpen={counterModalOpen}
        toggle={closeCounterModal}
        formData={counterFormData}
        saveDisabled={!counterHasChanges}
        errors={counterFormErrors}
        onFormChange={handleCounterFormChange}
        onSave={handleSaveCounter}
        loading={counterFormLoading}
        form={form}
        requiresCargoRatio={requiresCargoRatio}
        incotermTypeOptions={incotermTypeOptions}
        incotermRouteOptions={incotermRouteOptions}
        incotermLocationOptions={incotermLocationOptions}
        fobRouteOptions={fobRouteOptions}
      />
    </>
  );
};

export default ModelDeliveryTableLpg;
